import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingSmall,
    LabelMedium,
    LabelSmall,
    type Typography,
} from "@khanacademy/wonder-blocks-typography";
import {NICKNAME_MAX_LENGTH, UsernameTextField} from "@repo/auth-password";
import {RequestInfo} from "@repo/shared-core";
import {
    LanguageSelector,
    RegionSelector,
    type BasicSettingsData,
} from "@repo/user-settings";

import BirthdayPicker from "#components/birthday-picker/birthday-picker.tsx";

const StyledSpan = addStyle("span");

type Role =
    | "teacher"
    | "parent"
    | "learner"
    | "inferred_teacher"
    | "inferred_parent";

type OwnProps = {
    settings: BasicSettingsData;
    onSettingsChange: (
        // $ReadOnly is needed here since the callback that student-settings.jsx
        // passes us is a superset of the properties in BasicSettingsData.
        newSettings: Readonly<Partial<BasicSettingsData>>,
    ) => void;
    birthdateError: string | null | undefined;
    nicknameError: string | null | undefined;
    usernameError?: string | null | undefined;
    includesDistrictOwnedData: boolean;
    includesCentrallyOwnedDistrictData: boolean;
    disabled: boolean;
    // These were previously "required" (defaulted) by the class component
    // In the functional wrapper they are optional since they aren't defaulted
    actorIsChildAccount?: boolean;
    targetIsChildAccount?: boolean;
    roles?: Array<Role>;
    hasDistrictProvidedBirthdate: boolean;
    showUsernameField: boolean;
    isTeacherViewOfStudentSettings?: boolean;
    setUsernameError: (errorMessage: string | null) => void;
};

export type Props = OwnProps;

const StudentSettingsBasics: React.FC<Props> = (props) => {
    const {
        settings,
        onSettingsChange,
        birthdateError,
        nicknameError,
        includesDistrictOwnedData,
        includesCentrallyOwnedDistrictData,
        disabled,
        actorIsChildAccount,
        targetIsChildAccount,
        roles,
        hasDistrictProvidedBirthdate,
        showUsernameField,
        isTeacherViewOfStudentSettings,
        setUsernameError,
    } = props;

    const {nickname, username, userLanguage, region, birthdate} = settings;
    const initialBirthdate = React.useRef(birthdate);

    const targetIsParentOrTeacherAccount =
        roles && (roles.includes("teacher") || roles.includes("parent"));
    const targetIsOnEnglishSiteInUS =
        RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US";

    const renderUsernameDescription = (): React.ReactElement<
        React.ComponentProps<Typography>
    > => {
        // This is only an instructive link
        const profileUrl = "http://www.khanacademy.org/profile/";
        return (
            <LabelSmall style={styles.description}>
                {t`Your username will appear in your Khan Academy address.`}
                <Strut size={spacing.xxxxSmall_2} />
                {profileUrl}
                <StyledSpan style={styles.username}>{username}</StyledSpan>
            </LabelSmall>
        );
    };

    return (
        <React.Fragment>
            <HeadingSmall style={styles.header} tag="h2">
                {t`Basics`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            {/** Real name / nickname */}
            {!targetIsChildAccount && (
                <React.Fragment>
                    <LabeledTextField
                        testId="settings-nickname"
                        id="nickname"
                        label={
                            // For district users, regardless of whether they are centrally rostered or not,
                            // we want the teacher to see "Real name" because that's what that value should be.
                            includesDistrictOwnedData &&
                            isTeacherViewOfStudentSettings
                                ? t`Real name`
                                : t`Nickname`
                        }
                        description={t`This is how your name will appear around Khan Academy, and how your friends and coaches will recognize you.`}
                        value={nickname || ""}
                        onChange={(newValue) =>
                            onSettingsChange({
                                nickname: newValue.substring(
                                    0,
                                    NICKNAME_MAX_LENGTH,
                                ),
                            })
                        }
                        validate={() => nicknameError}
                        disabled={
                            disabled ||
                            // A district student rostered via the teacher-directed
                            // mechanism should have their name be editable by the teacher
                            // (i.e., not disabled).
                            (includesCentrallyOwnedDistrictData &&
                                isTeacherViewOfStudentSettings)
                        }
                        autoComplete="off"
                    />
                    <Strut size={spacing.medium_16} />
                </React.Fragment>
            )}
            {/** Username */}
            {showUsernameField && (
                <React.Fragment>
                    <UsernameTextField
                        testId="settings-username"
                        id="username"
                        label={t`Username`}
                        description={renderUsernameDescription()}
                        value={username || ""}
                        onChange={(newValue) =>
                            onSettingsChange({
                                username: newValue || null,
                            })
                        }
                        disabled={!!(disabled || actorIsChildAccount)}
                        onValidate={(errorMessage) => {
                            setUsernameError(errorMessage || null);
                        }}
                    />
                    <Strut size={spacing.medium_16} />
                </React.Fragment>
            )}
            {/** Birthdate: do not show if target role is teacher|parent */}
            {!targetIsParentOrTeacherAccount && (
                <React.Fragment>
                    <fieldset>
                        <LabelMedium style={styles.label} tag="legend">
                            {t`Birthdate`}
                        </LabelMedium>
                        <BirthdayPicker
                            onChange={(newBirthdate) => {
                                if (newBirthdate) {
                                    onSettingsChange({
                                        birthdate: newBirthdate,
                                    });
                                } else {
                                    // If newBirthdate is not truthy, it
                                    // means an invalid date was selected.
                                    // If an invalid date is selected, reset
                                    // the date to the initial value.
                                    onSettingsChange({
                                        birthdate: initialBirthdate.current,
                                    });
                                }
                            }}
                            disabled={
                                disabled ||
                                hasDistrictProvidedBirthdate ||
                                actorIsChildAccount
                            }
                            defaultValue={initialBirthdate.current || undefined}
                        />
                    </fieldset>
                    <View aria-live="polite">
                        {birthdateError && (
                            <LabelSmall style={styles.error}>
                                {birthdateError}
                            </LabelSmall>
                        )}
                    </View>
                    <Strut size={spacing.medium_16} />
                </React.Fragment>
            )}
            {/** Language */}
            <LanguageSelector
                userLanguage={userLanguage || "unspecified"}
                onChange={(userLanguage) => onSettingsChange({userLanguage})}
                disabled={disabled}
            />
            <Strut size={spacing.large_24} />
            {/** Region */}
            {targetIsOnEnglishSiteInUS && (
                <React.Fragment>
                    <RegionSelector
                        selectedRegionCode={region}
                        onChange={(region) => onSettingsChange({region})}
                    />
                    <Strut size={spacing.large_24} />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

StudentSettingsBasics.defaultProps = {
    actorIsChildAccount: false,
    targetIsChildAccount: false,
};

const disabledStyle = {
    backgroundColor: color.offWhite,
    color: color.offBlack64,
} as const;

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    label: {
        color: color.offBlack,
        marginBottom: spacing.small_12,
    },
    error: {
        color: color.red,
        marginTop: spacing.small_12,
    },
    description: {
        color: color.offBlack64,
        margin: 0,
    },
    username: {
        color: color.darkBlue,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    disabledStyle,
    // eslint-disable-next-line react-native/no-unused-styles
    errorDatePicker: {
        borderColor: color.red,
        ":focus": {
            borderColor: color.red,
        },
    },
});

export default StudentSettingsBasics;
